<script>
  import { onMount } from "svelte";
  import { navigate } from "svelte-routing";
  import { pies, currentPie } from "../../stores/pies.js";
  import CardPieChart from "../../components/Cards/CardPieChart.svelte";
  import api from "../../services/api.js";

  export let id;

  let loading = true;
  let rebalancing = false;
  let error = null;
  let success = null;

  // Edit mode
  let editMode = false;
  let editName = "";
  let editAllocations = [];
  let searchQuery = "";
  let searchResults = [];
  let searching = false;
  let saving = false;

  onMount(async () => {
    try {
      await pies.fetchPie(id);
    } catch (e) {
      error = e.message;
    }
    loading = false;
  });

  function startEdit() {
    editName = $currentPie.name;
    editAllocations = ($currentPie.allocations || []).map(a => ({ ...a }));
    editMode = true;
    error = null;
    success = null;
  }

  function cancelEdit() {
    editMode = false;
    editName = "";
    editAllocations = [];
    searchQuery = "";
    searchResults = [];
  }

  async function searchStocks() {
    if (!searchQuery || searchQuery.length < 1) {
      searchResults = [];
      return;
    }
    searching = true;
    try {
      searchResults = await api.searchStocks(searchQuery, 10);
    } catch (e) {
      console.error("Search failed:", e);
      searchResults = [];
    }
    searching = false;
  }

  function addStock(asset) {
    if (editAllocations.find(a => a.symbol === asset.symbol)) {
      error = `${asset.symbol} is already in the pie`;
      return;
    }
    editAllocations = [...editAllocations, { symbol: asset.symbol, percentage: 0 }];
    searchQuery = "";
    searchResults = [];
  }

  function removeStock(symbol) {
    editAllocations = editAllocations.filter(a => a.symbol !== symbol);
  }

  function updatePercentage(symbol, value) {
    const percentage = Math.max(0, Math.min(100, parseFloat(value) || 0));
    editAllocations = editAllocations.map(a =>
      a.symbol === symbol ? { ...a, percentage } : a
    );
  }

  $: editTotal = editAllocations.reduce((sum, a) => sum + a.percentage, 0);
  $: editValid = Math.abs(editTotal - 100) < 0.01;

  async function savePie() {
    if (!editValid) {
      error = "Allocations must sum to 100%";
      return;
    }
    if (!editName.trim()) {
      error = "Please enter a pie name";
      return;
    }
    saving = true;
    error = null;
    try {
      await pies.updatePie(id, {
        name: editName.trim(),
        allocations: editAllocations
      });
      await pies.fetchPie(id);
      success = "Pie updated successfully!";
      editMode = false;
    } catch (e) {
      error = e.message;
    }
    saving = false;
  }

  async function handleRebalance() {
    rebalancing = true;
    error = null;
    try {
      await pies.rebalancePie(id);
      success = "Rebalancing orders submitted successfully";
    } catch (e) {
      error = e.message;
    }
    rebalancing = false;
  }

  async function handleDelete() {
    if (!confirm("Are you sure you want to delete this pie?")) return;
    try {
      await pies.deletePie(id);
      navigate("/admin/pies");
    } catch (e) {
      error = e.message;
    }
  }

  $: chartData = ($currentPie?.allocations || []).map(a => ({
    symbol: a.symbol,
    percentage: a.percentage,
  }));
</script>

<header class="mb-6">
  <h1 class="text-2xl font-bold text-ink">Pie detail</h1>
  <p class="text-sm text-ink-secondary mt-1">Review holdings, edit targets, and rebalance this pie.</p>
</header>

{#if error}
  <div class="banner banner-danger mb-4 flex items-start justify-between gap-4">
    <span>{error}</span>
    <button on:click={() => error = null} class="shrink-0 text-red-800 hover:text-red-900">&times;</button>
  </div>
{/if}
{#if success}
  <div class="banner banner-success mb-4 flex items-start justify-between gap-4">
    <span>{success}</span>
    <button on:click={() => success = null} class="shrink-0 text-emerald-800 hover:text-emerald-900">&times;</button>
  </div>
{/if}

{#if loading}
  <div class="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
    <div class="xl:col-span-2 space-y-6">
      <div class="skeleton h-64"></div>
      <div class="skeleton h-36"></div>
    </div>
    <div class="skeleton h-96"></div>
  </div>
{:else if $currentPie}
  <div class="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
    <div class="xl:col-span-2 space-y-6">
      <div class="card overflow-hidden">
        <div class="p-6 flex items-start justify-between gap-4">
          <div class="flex-1 min-w-0">
            {#if editMode}
              <input
                type="text"
                bind:value={editName}
                class="input text-base font-semibold"
                placeholder="Pie name"
              />
            {:else}
              <h2 class="text-lg font-semibold text-ink truncate">
                {$currentPie.name}
              </h2>
            {/if}
            <p class="text-sm text-ink-secondary mt-1">{$currentPie.allocations?.length || 0} holdings</p>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            {#if editMode}
              <button on:click={cancelEdit} class="btn btn-ghost btn-sm">
                <i class="fas fa-times text-ink-tertiary"></i> Cancel
              </button>
            {:else}
              <button on:click={startEdit} class="btn btn-ghost btn-sm">
                <i class="fas fa-edit text-ink-tertiary"></i> Edit
              </button>
              <button on:click={handleDelete} class="btn btn-ghost btn-sm text-red-600 hover:text-red-700">
                <i class="fas fa-trash"></i> Delete
              </button>
            {/if}
          </div>
        </div>

        {#if editMode}
          <!-- Edit mode -->
          <div class="px-6 pb-6">
            <!-- Search to add stocks -->
            <div class="relative mb-4">
              <label class="label">
                Add stock
              </label>
              <div class="relative">
                <input
                  type="text"
                  bind:value={searchQuery}
                  on:input={searchStocks}
                  placeholder="Search by symbol or name..."
                  class="input"
                />
                {#if searching}
                  <div class="absolute right-4 top-1/2 -translate-y-1/2">
                    <i class="fas fa-spinner fa-spin text-ink-tertiary"></i>
                  </div>
                {/if}
              </div>
              {#if searchResults.length > 0}
                <div class="absolute z-10 w-full bg-surface border border-line rounded-xl shadow-sm mt-1 max-h-48 overflow-y-auto">
                  {#each searchResults as asset}
                    <button
                      on:click={() => addStock(asset)}
                      class="w-full text-left px-4 py-2.5 hover:bg-surface-variant flex justify-between items-center gap-3"
                    >
                      <span class="text-sm font-medium text-ink">{asset.symbol}</span>
                      <span class="text-sm text-ink-tertiary truncate">{asset.name}</span>
                    </button>
                  {/each}
                </div>
              {/if}
            </div>

            <!-- Allocation list -->
            <div class="space-y-2">
              {#each editAllocations as allocation}
                <div class="flex items-center gap-3 rounded-xl bg-surface-variant p-3">
                  <span class="text-sm font-semibold text-ink w-20 shrink-0">{allocation.symbol}</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={allocation.percentage}
                    on:input={(e) => updatePercentage(allocation.symbol, e.target.value)}
                    class="flex-1 accent-moira cursor-pointer"
                  />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={allocation.percentage}
                    on:input={(e) => updatePercentage(allocation.symbol, e.target.value)}
                    class="input !w-24 !py-2 text-right tnum !bg-surface"
                  />
                  <span class="text-sm text-ink-secondary">%</span>
                  <button
                    on:click={() => removeStock(allocation.symbol)}
                    class="px-1 text-ink-tertiary hover:text-red-600 transition-colors"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              {/each}
            </div>

            {#if editAllocations.length === 0}
              <p class="text-center text-sm text-ink-secondary py-4">No stocks added. Search above to add stocks.</p>
            {/if}

            <!-- Total and save -->
            <div class="mt-4 pt-4 border-t border-line flex items-center justify-between gap-4">
              <div class="flex items-baseline gap-2">
                <span class="text-sm font-medium text-ink">Total</span>
                <span class="text-lg font-semibold tnum {editValid ? 'text-emerald-600' : 'text-red-600'}">
                  {editTotal.toFixed(0)}%
                </span>
                {#if !editValid}
                  <span class="text-xs text-red-600">Must equal 100%</span>
                {/if}
              </div>
              <button
                on:click={savePie}
                disabled={!editValid || saving}
                class="btn btn-primary btn-sm"
              >
                {#if saving}
                  <i class="fas fa-spinner fa-spin"></i> Saving...
                {:else}
                  Save changes
                {/if}
              </button>
            </div>
          </div>
        {:else}
          <!-- View mode -->
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead>
                <tr>
                  <th class="th">
                    Symbol
                  </th>
                  <th class="th text-right">
                    Target %
                  </th>
                </tr>
              </thead>
              <tbody>
                {#each $currentPie.allocations || [] as allocation}
                  <tr class="hover:bg-canvas">
                    <td class="td">
                      <span class="font-medium">{allocation.symbol}</span>
                    </td>
                    <td class="td text-right tnum">
                      {allocation.percentage}%
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      </div>

      {#if !editMode}
        <div class="card card-pad">
          <h3 class="text-lg font-semibold text-ink mb-4">
            Actions
          </h3>

          <!-- Investment note -->
          <div class="banner banner-info mb-4">
            <i class="fas fa-info-circle mr-2"></i>
            To invest money, go to the <a href="/admin/pies" class="font-semibold underline hover:text-blue-900">Pies page</a> where you can invest across all your pies at once based on your allocation settings.
          </div>

          <div class="flex flex-wrap items-center gap-4">
            <button on:click={handleRebalance} disabled={rebalancing} class="btn btn-primary">
              {#if rebalancing}
                <i class="fas fa-spinner fa-spin"></i>
              {:else}
                <i class="fas fa-balance-scale"></i>
              {/if}
              Rebalance
            </button>
            <p class="text-xs text-ink-tertiary">
              Rebalancing will sell/buy positions to match target allocations
            </p>
          </div>
        </div>
      {/if}
    </div>

    <div>
      <CardPieChart title={$currentPie.name} data={chartData} />
    </div>
  </div>
{:else}
  <div class="card card-pad">
    <div class="flex flex-col items-center text-center py-12">
      <i class="fas fa-chart-pie text-2xl text-ink-tertiary mb-3"></i>
      <p class="text-sm text-ink-secondary">Pie not found</p>
    </div>
  </div>
{/if}
